import React, { useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query/react';
import dayjs from 'dayjs';

import { weeklyPlannerDayDisplayFormat } from '~libs/dayjs';

import { Button, ErrorResponse, ModalCard } from '~components';
import { Row, TableView } from '~components/table-view-v3';

import changesList from '~weekly-planner/forms/changes/list';

import { bulkUpdate } from '~appointments/actions/appointments';
import { useGetBuiltChangesQuery } from '~weekly-planner/actions/appointments';
import { unallocatedApi } from '~weekly-planner/actions/appointments/unallocated';
import { usersApi } from '~weekly-planner/actions/users';

import { clearSimulated as clearSimulatedClients } from '~weekly-planner/reducers/clients/clients';
import { clear, clearInteracting } from '~weekly-planner/reducers/unsaved';
import { clearSimulated as clearSimulatedUsers } from '~weekly-planner/reducers/users/users';
import { patchParams } from '~weekly-planner/reducers/weeklyPlanner';

import { selectErrorResponse } from '~appointments/selectors/appointments';
import { selectLookupUsers } from '~care-workers/selectors/users';
import { selectAllChanges } from '~weekly-planner/selectors/unsaved';

interface ComponentProps {
  isOpen?: boolean;
  onClose: () => void;
  weekStart: any;
}

interface Changes {
  start_time?: string;
  end_time?: string;
  date?: string;
  user_id?: number;
  notes_admin?: string;
}

const Changes: React.FC<ComponentProps> = ({ isOpen = false, onClose, weekStart }: ComponentProps) => {
  const dispatch = useAppDispatch();
  const errorMessage = useAppSelector(selectErrorResponse);
  const users = useAppSelector(selectLookupUsers);

  const unsavedChanges = useAppSelector(selectAllChanges);

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const hasError = errorMessage ? errorMessage.length > 0 : false;

  const changedIds = useMemo(() => {
    const ids = Object.values(unsavedChanges)
      .map(({ change }) => change.id ?? change.parent_repeat_id)
      .filter((v): v is number => v != null);
    return Array.from(new Set(ids));
  }, [unsavedChanges]);

  const args = isOpen && changedIds.length > 0 ? { id: changedIds, week_start: weekStart } : skipToken;
  const { isFetching, isLoading } = useGetBuiltChangesQuery(args);

  // Compare pre & post update values, determine if changed & return
  const buildChange = (original: string | undefined, change: string | undefined) => {
    if (!change || original === change) return { from: original || null, to: null, updated: false };
    return { from: original || null, to: change, updated: true };
  };

  // Build appointment pre & post update info for display.
  const builtAppointments = Object.values(unsavedChanges)
    .map(({ change, original }): Row | null => {
      if (!change) return null;

      const { start_time, end_time, user_id: changedUserId, notes_admin } = change;

      // Determine which userId to display (from change or original snapshot)
      const displayUserId = 'user_id' in change ? changedUserId : original?.user_id;

      // Find user from displayUserId
      const displayUser = users.find((user) => user.id === displayUserId);

      // For the "from" value, prefer the original.user if available, otherwise fallback to users list
      const originalUserName =
        original?.user?.full_name ?? users.find((user) => user.id === original?.user_id)?.name ?? '-';

      const isNew = !original.id;
      // Guarantee a row id for the table - matches the fallback pattern in the sidebar lists
      const id = original?.id ?? original?.parent_repeat_id ?? crypto.randomUUID();

      return {
        id,
        isNew,
        client: {
          from: original?.client.full_name,
          to: null,
        },
        service: {
          from: original?.service_type.name,
          to: null,
        },
        date: buildChange(
          dayjs(original?.date).format(weeklyPlannerDayDisplayFormat),
          'date' in change && change.date ? dayjs(change.date).format(weeklyPlannerDayDisplayFormat) : '',
        ),
        time: buildChange(
          `${original?.start_time} - ${original?.end_time}`,
          !start_time || !end_time ? '' : `${start_time} - ${end_time}`,
        ),
        worker: buildChange(originalUserName, displayUser ? displayUser.name : ''),
        notes_admin,
      };
    })
    .filter((appt): appt is Row => appt !== null);

  const onSave = async () => {
    setIsSaving(true);

    const changesToSave = Object.values(unsavedChanges).map(({ change }) => change);
    const { payload } = await dispatch(bulkUpdate(changesToSave));

    setIsSaving(false);
    if (payload?.success) {
      // Reset unsaved changes
      dispatch(clearInteracting());
      dispatch(clear());
      dispatch(clearSimulatedUsers([]));
      dispatch(clearSimulatedClients([]));
      dispatch(patchParams({ limit: 30 }));

      // Force refetch of unallocated appointments & users
      dispatch(unallocatedApi.util.invalidateTags([{ type: 'unallocated', id: 'LIST' }]));
      dispatch(usersApi.util.invalidateTags([{ type: 'users', id: 'LIST' }]));

      onClose();
    }
  };

  const modalProps = {
    className: 'border-0 rounded-0 mt-1 mb-1',
    headerClassName: 'rounded-0',
    bodyClassName: 'changelog-body',
  };

  return (
    <Modal isOpen={isOpen} className="change-modal" unmountOnClose>
      <ModalHeader className="bg-success text-white">Review Changes</ModalHeader>
      <ModalBody>
        <div className="mb-4 pb-2 border-2 border-bottom border-success">
          Please confirm the following changes to save...
        </div>
        <ErrorResponse message={errorMessage} />
        {(isFetching || isLoading) && (
          <div className="d-flex align-items-center mb-2">
            <Spinner size="sm" className="me-2" /> Loading change details…
          </div>
        )}
        <ModalCard {...modalProps} header="Show changes">
          <TableView id="changes" data={builtAppointments} columns={changesList} readonly striped />
        </ModalCard>
      </ModalBody>
      <ModalFooter className="align-items-center flex-column">
        <div>
          <Button size="sm" disabled={isSaving || hasError} color="success" onClick={() => onSave()}>
            {isSaving && <Spinner type="grow" size="sm" className="ms-4 me-4" />}
            {!isSaving && <span>Save</span>}
          </Button>
          <Button size="sm" className="ms-2 ps-4 pe-4" onClick={() => onClose()}>
            Close
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default Changes;
